import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

async function fetchServiceDisruptionBreakdown(breakdownType, queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/service-disruption/breakdown?breakdown=${breakdownType}&${queryString}`;
  const response = await api.get(url);
  return response.data;
}

function useServiceDisruptionBreakdown(breakdownType, filters, dateRange, page = 1, pageSize = 50) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Build query string
  const params = new URLSearchParams();
  ['domain', 'group', 'unit'].forEach(key => {
    (filters?.[key] || []).forEach(value => params.append(key, value));
  });

  const formatDate = date => {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  if (dateRange?.startDate) {
    const fromStr = formatDate(dateRange.startDate);
    if (fromStr) params.set('from', fromStr);
  }
  if (dateRange?.endDate) {
    const toStr = formatDate(dateRange.endDate);
    if (toStr) params.set('to', toStr);
  }

  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);

  const queryString = params.toString();

  const fetchBreakdown = useCallback(async (isCancelled = () => false) => {
    // Don't load if no date range
    if (!dateRange?.startDate || !dateRange?.endDate) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const breakdown_data = await fetchServiceDisruptionBreakdown(breakdownType, queryString);
      if (!isCancelled()) setData(breakdown_data);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [breakdownType, queryString, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    let cancelled = false;
    fetchBreakdown(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchBreakdown]);

  return { data, loading, error, refetch: () => fetchBreakdown() };
}

export default useServiceDisruptionBreakdown;
